import { useCallback, useMemo, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useDecision } from '@optimizely/react-sdk';
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk';
import { useNavigate } from 'react-router-dom';
import { identify, useTrackOptimizely } from '@fff-web/fff-analytics';
import { HCaptchaContainer } from '@fff-web/fff-payments';
import { Button, showErrorToast } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import { PromotionBannerContainer } from '../../components/PromotionBannerContainer';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import { getBillingInfo } from '../../pages/MyAccount/services/api/billing';
import { reactivateExpiredSubscription } from '../../services/subscription';
import { type IReactivateMembershipContext } from '../../utils/ContextProviders/ReactivateContext';
import { useReactivatePlanDataContext } from '../../utils/ContextProviders/ReactivatePlanDataContext';
import { executeCaptcha } from '../../utils/functions/executeCaptcha';
import { extractErrorDetails } from '../../utils/functions/extractErrorDetails';
import useGetPricesByPlan from '../../utils/hooks/useGetPricesByPlan';
import { GROWTH_MY_REACTIVATE_MEMBERSHIP } from '../../utils/Optimizely/featureFlagVariations';
import env from '../MyAccount/utils/env';
import { sendGTMReactivateConversion, sendReactivateSegmentEventAfterResponse } from './Events';
import HeroBannerContainer from './HeroBannerContainer';
import HowItWorks from './HowItWorks';
import MembershipPlans from './MembershipPlans';
import { PriceSummary } from './PriceSummary';
import { ReactivateCarousel } from './ReactivateCarousel';
import {
  getErrorDetailsFromReactivateResponse,
  getReactivateErrorToastMessage,
  hasMappedReactivateErrorToastMessage,
  REACTIVATE_ERROR_CODE_LOG_MESSAGE,
  REACTIVATE_ERROR_TOAST_TITLE,
  REACTIVATE_UNMAPPED_ERROR_CODE_LOG_MESSAGE,
} from './reactivateMembershipUtils';
import { ReactivationMessages } from './ReactivateUtils';
import TermsAndConditions from './TermsAndConditions';
import { UpdatePaymentModalContainer } from './UpdatePaymentModalContainer/';
import * as styles from './ReactivateMembership.module.scss';

const PAGE_TITLE = 'Reactivate Membership';
const NO_PAYMENT_METHOD_TOAST = {
  title: 'Update Payment Method',
  content: [
    {
      text: 'We were unable to verify your payment method. Please',
    },
    {
      url: '/my-account',
      text: 'update your payment information',
    },
    {
      text: 'to continue.',
    },
  ],
};
const ReactivateMembership = () => {
  const navigate = useNavigate();
  const { userInfo } = useUserContext();
  const { isLoadingPrices } = useGetPricesByPlan();
  const [growthMyMembershipDecision, isReady] = useDecision(
    GROWTH_MY_REACTIVATE_MEMBERSHIP.FEATURE_FLAG,
  );

  const { variables } = growthMyMembershipDecision as IReactivateMembershipContext;
  const planSectionRef = useRef<HTMLDivElement>(null);
  const captchaRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const [submitPactSafeContract, setSubmitPactSafeContract] = useState(false);
  const [isLoadingBillingInfo, setIsLoadingBillingInfo] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(true);
  const { howItWorks } = variables;
  const thanksPage = '/reactivate-thanks';

  useTrackOptimizely(
    growthMyMembershipDecision,
    growthMyMembershipDecision.ruleKey || '',
    userInfo?.loggedIn && isReady,
  );

  const scrollToPlanSection = () => {
    const section = planSectionRef.current;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const { couponData, selectedPlanCode, promoCodeType } = useReactivatePlanDataContext();

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(navigate, thanksPage, couponData?.code);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setIsLoadingBillingInfo(true);
      const billingInfo = await getBillingInfo();
      if (!billingInfo.paymentMethod) {
        setIsLoadingBillingInfo(false);
        setHasPaymentMethod(false);
        showErrorToast(NO_PAYMENT_METHOD_TOAST.title, NO_PAYMENT_METHOD_TOAST.content);
        return;
      }

      const captchaToken = await executeCaptcha(captchaRef);
      memoizedIsCheckboxChecked();
      setLoading(true);

      const response = await reactivateExpiredSubscription(
        selectedPlanCode,
        couponData?.code,
        captchaToken,
        promoCodeType,
      );
      if (!response) {
        throw new Error('Reactivate request returned an empty response');
      }

      const status = getPlanChangeCompletedEventStatusFromResponse(response);
      const eventsToFire = [
        sendReactivateSegmentEventAfterResponse(status, ReactivationMessages.SUBMIT_CTA_NAME),
      ];

      if (status === 'success') {
        eventsToFire.push(identify({ traits: { planCode: selectedPlanCode } }));
        sendGTMReactivateConversion(response);
      } else {
        const { errorCode, errorMessage } = await getErrorDetailsFromReactivateResponse(response);
        if (errorCode) {
          datadogLogs.logger.info(REACTIVATE_ERROR_CODE_LOG_MESSAGE, { errorCode });
          if (!hasMappedReactivateErrorToastMessage(errorCode)) {
            datadogLogs.logger.warn(REACTIVATE_UNMAPPED_ERROR_CODE_LOG_MESSAGE, { errorCode });
          }
        }
        const errorToastMessage = getReactivateErrorToastMessage(errorCode, errorMessage);

        if (errorToastMessage) {
          showErrorToast(REACTIVATE_ERROR_TOAST_TITLE, errorToastMessage);
        }
      }

      await Promise.allSettled(eventsToFire);
      await handlePlanChangeResponse(response);
    } catch (error) {
      const { errorCode, errorMessage } = extractErrorDetails(error);
      if (errorCode) {
        datadogLogs.logger.info(REACTIVATE_ERROR_CODE_LOG_MESSAGE, { errorCode });
        if (!hasMappedReactivateErrorToastMessage(errorCode)) {
          // DD log to identify if we need to add a new error code to the toast mapping
          datadogLogs.logger.warn(REACTIVATE_UNMAPPED_ERROR_CODE_LOG_MESSAGE, { errorCode });
        }
      }
      const errorToastMessage = getReactivateErrorToastMessage(errorCode, errorMessage);
      const subscriptionError =
        error instanceof Error ? error : new Error('Unknown reactivation error');

      if (errorToastMessage) {
        showErrorToast(REACTIVATE_ERROR_TOAST_TITLE, errorToastMessage);
      }

      datadogLogs.logger.error(ReactivationMessages.SUBSCRIPTION_ERROR, {}, subscriptionError);
      setLoading(false);
    } finally {
      setIsLoadingBillingInfo(false);
      isSubmittingRef.current = false;
    }
  };

  const clickWrapConfig = {
    accessId: env.pactSafeAccessId,
    groupKey: env.pactSafeGroupKey,
    groupId: env.pactSafeGroupId,
    signerId: userInfo?.user.email,
    testMode: env.appEnv === 'dev',
    snapshotLocation: env.pactSafeSnapshotId,
  };

  const isCheckboxChecked = useCallback(() => {
    const checkboxElement = document.getElementById(
      `ps-contract-checkbox-group-${clickWrapConfig?.groupId}`,
    ) as HTMLInputElement;
    if (clickWrapConfig?.signerId && checkboxElement && !checkboxElement.checked) {
      setTimeout(() => {
        checkboxElement.click();
        checkboxElement.disabled = true;
        setSubmitPactSafeContract(true);
      }, 1000);
    }
    return checkboxElement;
  }, [clickWrapConfig?.groupId, clickWrapConfig?.signerId]);

  const memoizedIsCheckboxChecked = useMemo(() => isCheckboxChecked, [isCheckboxChecked]);

  return (
    <div>
      <PromotionBannerContainer onClick={scrollToPlanSection} />
      <Layout
        isLoading={isLoadingPrices || isLoading}
        pageTitle={PAGE_TITLE}
        redirectIfNoAuth
        showError={showRetry}
        ErrorComponent={() => (
          <AllErrorMessage subjectAction="reactivate" onButtonClick={onClose} />
        )}
        hideChatWidget={!showPayment}
      >
        <main className={styles.reactivateMainWrapper}>
          <HeroBannerContainer onCTAClick={scrollToPlanSection} />
          <HowItWorks headline={howItWorks?.headline} productsList={howItWorks?.productsList} />
          <ReactivateCarousel cardClickHandler={scrollToPlanSection} />
          <MembershipPlans planSectionRef={planSectionRef} />
          <section className={styles.summarySectionWrapper}>
            <PriceSummary />
            <TermsAndConditions />
          </section>
          <section className={styles.ctaSectionWrapper}>
            <Button
              disabled={isLoading || isLoadingBillingInfo || !hasPaymentMethod}
              onClick={handleSubmit}
            >
              {ReactivationMessages.SUBMIT_CTA_NAME}
            </Button>
          </section>
          <UpdatePaymentModalContainer
            onClose={onClose}
            subtitle={errorPaymentMessage}
            isOpen={showPayment}
            onSuccess={handleSubmit}
            hasError={!!errorPaymentMessage}
          />
          <HCaptchaContainer ref={captchaRef} />
          <div className={styles.pactsafeWrapper}>
            <PSClickWrap
              signerId={clickWrapConfig.signerId}
              accessId={clickWrapConfig.accessId}
              groupKey={clickWrapConfig.groupKey}
              snapshotLocation={clickWrapConfig.snapshotLocation}
              disableSending={submitPactSafeContract}
              displayImmediately={false}
              testMode={clickWrapConfig.testMode}
              clickWrapStyle="combined"
            />
          </div>
        </main>
      </Layout>
    </div>
  );
};

export default ReactivateMembership;
